import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ClinicalOnboardingService } from './clinical-onboarding.service';
import { AlertService } from './general/alert.service';
import { ToastService } from './general/toast.service';
import {
  ModalRegisterClinicianPaymentComponent
} from '../modals/modal-register-clinician-payment/modal-register-clinician-payment.component';
import {
  ModalClinicianStateReasonComponent
} from '../modals/modal-clinician-state-reason/modal-clinician-state-reason.component';

/** Il clinico su cui agire: costruito dalla riga della tabella o dal dettaglio nel dialog. */
export interface ClinicianStateSubject {
  surveyId: string | number;
  candidateId: number;
  name: string;
  isPaid: boolean;
  isExcluded: boolean;
  hasParticipationOverride: boolean;
  /** Importo precompilato registrando il pagamento: compenso netto inserito, altrimenti la stima. */
  suggestedAmount: number | null;
  payment: { paid_amount: number | null; payment_date: string | null; cro_reference: string | null } | null;
}

export interface ClinicianStateActionHooks {
  /** true quando parte una chiamata, false quando finisce (per disabilitare i pulsanti). */
  onBusy?: (busy: boolean) => void;
  /** A cambio di stato riuscito: chi chiama ricarica i propri dati. */
  onDone: () => void;
}

/**
 * Azioni di stato del gestore su un clinico - pagamento (registra / modifica /
 * annulla), esclusione / riammissione, forzatura della partecipazione - condivise
 * dal menu a tre puntini della tabella onboarding e dalla tab "Dati contrattuali"
 * del dialog di dettaglio, cosi' il comportamento (conferme, motivo facoltativo,
 * messaggi) resta identico dai due punti. Il macro-stato lo ricalcola il server.
 */
@Injectable({
  providedIn: 'root',
})
export class ClinicalOnboardingStateActionsService {
  constructor(
    private dialog: MatDialog,
    private alertService: AlertService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private clinicalOnboardingService: ClinicalOnboardingService,
  ) {}

  registerPayment(subject: ClinicianStateSubject, hooks: ClinicianStateActionHooks): void {
    this.openPaymentDialog(subject, hooks, {
      surveyId: subject.surveyId,
      candidateId: subject.candidateId,
      name: subject.name,
      suggestedAmount: subject.suggestedAmount,
    });
  }

  editPayment(subject: ClinicianStateSubject, hooks: ClinicianStateActionHooks): void {
    this.openPaymentDialog(subject, hooks, {
      surveyId: subject.surveyId,
      candidateId: subject.candidateId,
      name: subject.name,
      suggestedAmount: null,
      payment: subject.payment,
    });
  }

  cancelPayment(subject: ClinicianStateSubject, hooks: ClinicianStateActionHooks): void {
    this.alertService.showQuestion(
      this.translateService.instant('components.clinical-onboarding.dashboard.cancel-payment-title'),
      this.translateService.instant('components.clinical-onboarding.dashboard.cancel-payment-text', { name: subject.name }),
      () => this.run(hooks, (done, fail) =>
        this.clinicalOnboardingService.cancelClinicianPayment(subject.surveyId, subject.candidateId, done, fail)),
    );
  }

  toggleExclusion(subject: ClinicianStateSubject, hooks: ClinicianStateActionHooks): void {
    if (subject.isExcluded) {
      this.run(hooks, (done, fail) =>
        this.clinicalOnboardingService.setClinicianExclusion(subject.surveyId, subject.candidateId, false, null, done, fail));
      return;
    }

    this.askReason(
      subject,
      'components.clinical-onboarding.dashboard.exclude-title',
      'components.clinical-onboarding.dashboard.exclude-text',
      (reason) => this.run(hooks, (done, fail) =>
        this.clinicalOnboardingService.setClinicianExclusion(subject.surveyId, subject.candidateId, true, reason, done, fail)),
    );
  }

  toggleParticipationOverride(subject: ClinicianStateSubject, hooks: ClinicianStateActionHooks): void {
    if (subject.hasParticipationOverride) {
      this.run(hooks, (done, fail) =>
        this.clinicalOnboardingService.setClinicianParticipationOverride(subject.surveyId, subject.candidateId, false, null, done, fail));
      return;
    }

    this.askReason(
      subject,
      'components.clinical-onboarding.dashboard.override-title',
      'components.clinical-onboarding.dashboard.override-text',
      (reason) => this.run(hooks, (done, fail) =>
        this.clinicalOnboardingService.setClinicianParticipationOverride(subject.surveyId, subject.candidateId, true, reason, done, fail)),
    );
  }

  private openPaymentDialog(subject: ClinicianStateSubject, hooks: ClinicianStateActionHooks, data: any): void {
    const dialogRef = this.dialog.open(ModalRegisterClinicianPaymentComponent, { data, panelClass: 'medium-dialog' });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        hooks.onDone();
      }
    });
  }

  /** Conferma con un motivo FACOLTATIVO (testo semplice): null se lasciato vuoto. */
  private askReason(subject: ClinicianStateSubject, titleKey: string, textKey: string, onConfirm: (reason: string | null) => void): void {
    const dialogRef = this.dialog.open(ModalClinicianStateReasonComponent, {
      data: { titleKey, textKey, name: subject.name },
      panelClass: 'medium-dialog',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        onConfirm(result.reason);
      }
    });
  }

  private run(hooks: ClinicianStateActionHooks, action: (done: () => void, fail: () => void) => void): void {
    hooks.onBusy?.(true);
    action(
      () => {
        hooks.onBusy?.(false);
        this.toastService.success(
          this.translateService.instant('generic.success'),
          this.translateService.instant('components.clinical-onboarding.clinician-detail.data-saved'),
        );
        hooks.onDone();
      },
      () => hooks.onBusy?.(false),
    );
  }
}
